/**
 * Admin — Travel Intake management.
 * Route: /admin/intake
 *
 * Two tabs:
 *  1. Links — create tokenised intake links, copy the URL, email the client.
 *  2. Submissions — inbox of completed intake forms, view details, mark
 *     reviewed/converted, export PDF.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Copy, Send, Ban, Eye, FileDown, ArrowLeft, ClipboardList, Inbox, X, Trash2, ArrowRightCircle, CheckCircle2, MessageCircle, ExternalLink, Bell, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Basic ${btoa(`:${sessionStorage.getItem('admin_password') || ''}`)}` });

export default function IntakeManager() {
  const [tab, setTab] = useState('links');
  const [links, setLinks] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newLink, setNewLink] = useState({ client_name: '', client_email: '', client_phone: '', expires_in_days: 30 });
  const [viewSub, setViewSub] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/intake-links`, { headers: auth() }),
        axios.get(`${API}/admin/intake-submissions`, { headers: auth() }),
      ]);
      setLinks(l.data || []);
      setSubs(s.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load intake data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const publicUrl = (token) => `${window.location.origin}/intake/${token}`;

  const createLink = async () => {
    if (!newLink.client_name && !newLink.client_email) {
      toast.error('Enter a client name or email');
      return;
    }
    try {
      await axios.post(`${API}/admin/intake-links`, newLink, { headers: auth() });
      toast.success('Intake link created');
      setCreating(false);
      setNewLink({ client_name: '', client_email: '', client_phone: '', expires_in_days: 30 });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create link');
    }
  };

  const sendLink = async (link) => {
    if (!link.client_email && !window.prompt('No email on file. Type recipient email:')) return;
    try {
      await axios.post(`${API}/admin/intake-links/${link.id}/send`, {}, { headers: auth() });
      toast.success(`Emailed to ${link.client_email}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    }
  };

  const revokeLink = async (link) => {
    if (!window.confirm('Revoke this link? The client won\'t be able to submit.')) return;
    try {
      await axios.delete(`${API}/admin/intake-links/${link.id}`, { headers: auth() });
      toast.success('Link revoked');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revoke');
    }
  };

  const remindLink = async (link) => {
    try {
      await axios.post(`${API}/admin/intake-links/${link.id}/remind`, {}, { headers: auth() });
      toast.success(`Reminder emailed to ${link.client_email}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const runAllReminders = async () => {
    if (!window.confirm('Send reminders to every client whose link is 5+ days old and unsubmitted?')) return;
    try {
      const { data } = await axios.post(`${API}/admin/intake-reminders/run`, {}, { headers: auth() });
      toast.success(`Scanned ${data.scanned} · Sent ${data.sent} · Skipped ${data.skipped}`);
      reload();
    } catch (e) {
      toast.error('Reminder job failed');
    }
  };

  const copyUrl = (token) => {
    navigator.clipboard.writeText(publicUrl(token));
    toast.success('Link copied to clipboard');
  };

  const openWhatsApp = (link) => {
    const phone = (link.client_phone || '').replace(/\D/g, '');
    if (!phone) {
      const p = window.prompt('No phone number on file. Enter recipient phone (with country code, digits only):');
      if (!p) return;
      const cleaned = p.replace(/\D/g, '');
      if (!cleaned) return;
      return openWhatsAppWith(cleaned, link);
    }
    openWhatsAppWith(phone, link);
  };

  const openWhatsAppWith = (phone, link) => {
    const name = link.client_name ? link.client_name.split(' ')[0] : 'there';
    const url = publicUrl(link.token);
    const expires = (link.expires_at || '').slice(0, 10);
    const text = `Hi ${name}! Please complete your Pawsome Ventures travel details form using this secure link:\n\n${url}\n\nIt should take about 10 minutes on your phone. Link expires ${expires}. — Pawsome Ventures`;
    const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank', 'noopener');
  };

  const downloadPdf = async (sub) => {
    try {
      const { data } = await axios.get(`${API}/admin/intake-submissions/${sub.id}/pdf`, { headers: auth(), responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `intake-${(sub.client_name || 'client').replace(/\s+/g, '_')}-${sub.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download PDF');
    }
  };

  const downloadAuditPdf = async (sub) => {
    try {
      const { data } = await axios.get(`${API}/admin/intake-submissions/${sub.id}/signature-audit-pdf`, { headers: auth(), responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signature-audit-intake-${(sub.client_name || 'client').replace(/\s+/g, '_')}-${sub.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to download signature audit');
    }
  };

  const setSubStatus = async (sub, status) => {
    try {
      await axios.put(`${API}/admin/intake-submissions/${sub.id}/status`, { status }, { headers: auth() });
      toast.success(`Marked as ${status}`);
      reload();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const deleteSub = async (sub) => {
    if (!window.confirm(`Delete submission from ${sub.client_name || sub.client_email}?`)) return;
    try {
      await axios.delete(`${API}/admin/intake-submissions/${sub.id}`, { headers: auth() });
      toast.success('Submission deleted');
      setViewSub(null);
      reload();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const convertSub = async (sub) => {
    const petCount = (sub.pets || []).length;
    if (!window.confirm(`Convert this submission into ${petCount} Relocation record${petCount === 1 ? '' : 's'} + Pet folder${petCount === 1 ? '' : 's'} for ${sub.client_name || sub.consignor?.full_name}?`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/intake-submissions/${sub.id}/convert`, {}, { headers: auth() });
      toast.success(`Created ${data.relocation_ids.length} relocation(s) + ${data.pet_ids.length} pet folder(s)`);
      // Update the local viewSub so the drawer shows the "Converted" chip immediately
      setViewSub(prev => prev ? { ...prev, status: 'converted', conversion: data } : prev);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to convert submission');
    }
  };

  const pendingCount = useMemo(() => subs.filter(s => s.status === 'new').length, [subs]);

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="bg-white border-b">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-gray-500 hover:text-gray-700" data-testid="back-to-dashboard"><ArrowLeft className="w-5 h-5" /></Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Travel Intake</h1>
              <p className="text-sm text-gray-500">Send clients a link to complete travel details on their phone or laptop.</p>
            </div>
          </div>
          <button onClick={() => setCreating(true)} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-2" data-testid="create-intake-link-btn">
            <Plus className="w-4 h-4" /> New intake link
          </button>
          <button onClick={runAllReminders} className="px-3 py-2 rounded-lg border-2 border-amber-500 text-amber-700 hover:bg-amber-50 font-semibold flex items-center gap-2 text-sm" title="Auto-nudge all clients whose link is 5+ days old" data-testid="run-reminders-btn">
            <Zap className="w-4 h-4" /> Nudge stale
          </button>
        </div>
        <div className="container mx-auto px-6 flex gap-1">
          <TabButton active={tab === 'links'} onClick={() => setTab('links')} icon={<ClipboardList className="w-4 h-4" />} label="Links" count={links.filter(l => l.status === 'pending').length} testId="tab-links" />
          <TabButton active={tab === 'submissions'} onClick={() => setTab('submissions')} icon={<Inbox className="w-4 h-4" />} label="Submissions" count={pendingCount} highlight testId="tab-submissions" />
        </div>
      </div>

      <div className="container mx-auto px-6 py-6">
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-emerald-600" /></div>
        ) : tab === 'links' ? (
          <LinksTable links={links} publicUrl={publicUrl} onCopy={copyUrl} onSend={sendLink} onWhatsApp={openWhatsApp} onRemind={remindLink} onRevoke={revokeLink} />
        ) : (
          <SubmissionsTable subs={subs} onView={setViewSub} onPdf={downloadPdf} onStatus={setSubStatus} />
        )}
      </div>

      {creating && <CreateLinkModal link={newLink} setLink={setNewLink} onCancel={() => setCreating(false)} onCreate={createLink} />}
      {viewSub && <ViewSubmissionModal sub={viewSub} onClose={() => setViewSub(null)} onPdf={downloadPdf} onAuditPdf={downloadAuditPdf} onDelete={deleteSub} onStatus={setSubStatus} onConvert={convertSub} />}
    </div>
  );
}

const TabButton = ({ active, onClick, icon, label, count, highlight, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`px-4 py-3 flex items-center gap-2 text-sm font-medium border-b-2 -mb-px ${active ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
    {icon} {label}
    {count > 0 && <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${highlight ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}>{count}</span>}
  </button>
);

const StatusPill = ({ status }) => {
  const map = {
    pending: 'bg-amber-50 text-amber-800 border-amber-200',
    submitted: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    revoked: 'bg-gray-100 text-gray-600 border-gray-200',
    expired: 'bg-gray-100 text-gray-500 border-gray-200',
    new: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    reviewed: 'bg-blue-50 text-blue-800 border-blue-200',
    converted: 'bg-purple-50 text-purple-800 border-purple-200',
  };
  return <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
};

const LinksTable = ({ links, publicUrl, onCopy, onSend, onWhatsApp, onRemind, onRevoke }) => (
  <div className="bg-white rounded-xl border overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b">
        <tr>
          <Th>Client</Th><Th>Status</Th><Th>Created</Th><Th>Expires</Th><Th>URL</Th><Th className="text-right">Actions</Th>
        </tr>
      </thead>
      <tbody>
        {links.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-500">No intake links yet — click "New intake link" above to send one.</td></tr>}
        {links.map(l => (
          <tr key={l.id} className="border-b hover:bg-gray-50" data-testid={`link-row-${l.id}`}>
            <td className="px-4 py-3">
              <div className="font-medium text-gray-900">{l.client_name || '—'}</div>
              <div className="text-xs text-gray-500">{l.client_email || '—'}</div>
              {l.client_phone && <div className="text-xs text-gray-400">{l.client_phone}</div>}
            </td>
            <td className="px-4 py-3"><StatusPill status={l.status} /></td>
            <td className="px-4 py-3 text-gray-600">{(l.created_at || '').slice(0, 10)}</td>
            <td className="px-4 py-3 text-gray-600">{(l.expires_at || '').slice(0, 10)}</td>
            <td className="px-4 py-3 max-w-xs truncate text-xs text-gray-500">{publicUrl(l.token)}</td>
            <td className="px-4 py-3 text-right whitespace-nowrap">
              <button title="Copy URL" onClick={() => onCopy(l.token)} className="p-1.5 text-gray-500 hover:text-emerald-700"><Copy className="w-4 h-4" /></button>
              {l.status === 'pending' && (
                <button title="Send via WhatsApp" onClick={() => onWhatsApp(l)} className="p-1.5 text-gray-500 hover:text-green-600" data-testid={`whatsapp-${l.id}`}><MessageCircle className="w-4 h-4" /></button>
              )}
              {l.status === 'pending' && l.client_email && (
                <button title="Email client" onClick={() => onSend(l)} className="p-1.5 text-gray-500 hover:text-emerald-700" data-testid={`send-${l.id}`}><Send className="w-4 h-4" /></button>
              )}
              {l.status === 'pending' && l.client_email && (
                <button title="Send reminder" onClick={() => onRemind(l)} className="p-1.5 text-gray-500 hover:text-amber-600" data-testid={`remind-${l.id}`}><Bell className="w-4 h-4" /></button>
              )}
              {l.status === 'pending' && (
                <button title="Revoke" onClick={() => onRevoke(l)} className="p-1.5 text-gray-500 hover:text-red-600"><Ban className="w-4 h-4" /></button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SubmissionsTable = ({ subs, onView, onPdf, onStatus }) => (
  <div className="bg-white rounded-xl border overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b">
        <tr>
          <Th>Client</Th><Th>Pets</Th><Th>Departure</Th><Th>Status</Th><Th>Submitted</Th><Th className="text-right">Actions</Th>
        </tr>
      </thead>
      <tbody>
        {subs.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-500">No submissions yet.</td></tr>}
        {subs.map(s => (
          <tr key={s.id} className="border-b hover:bg-gray-50" data-testid={`sub-row-${s.id}`}>
            <td className="px-4 py-3">
              <div className="font-medium text-gray-900">{s.client_name || s.consignor?.full_name || '—'}</div>
              <div className="text-xs text-gray-500">{s.client_email || s.consignor?.email || '—'}</div>
              {s.status === 'converted' && s.conversion?.relocation_ids?.length > 0 && (
                <a
                  href={`/admin/relocations/${s.conversion.relocation_ids[0]}`}
                  className="inline-flex items-center gap-1 text-xs text-purple-700 bg-purple-50 hover:bg-purple-100 px-2 py-0.5 rounded-full mt-1"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`relocation-chip-${s.id}`}
                >
                  → Relocation {s.conversion.relocation_ids[0].slice(0, 6)}
                  {s.conversion.relocation_ids.length > 1 && ` +${s.conversion.relocation_ids.length - 1}`}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </td>
            <td className="px-4 py-3 text-gray-700">{(s.pets || []).map(p => p.name || '?').join(', ') || '—'}</td>
            <td className="px-4 py-3 text-gray-600">{s.travel?.departure_date || '—'} → {s.travel?.arrival_airport || '—'}</td>
            <td className="px-4 py-3"><StatusPill status={s.status} /></td>
            <td className="px-4 py-3 text-gray-600">{(s.submitted_at || '').slice(0, 10)}</td>
            <td className="px-4 py-3 text-right whitespace-nowrap">
              <button title="View" onClick={() => onView(s)} className="p-1.5 text-gray-500 hover:text-emerald-700" data-testid={`view-${s.id}`}><Eye className="w-4 h-4" /></button>
              <button title="Download PDF" onClick={() => onPdf(s)} className="p-1.5 text-gray-500 hover:text-emerald-700"><FileDown className="w-4 h-4" /></button>
              {s.status === 'new' && (
                <button title="Mark reviewed" onClick={() => onStatus(s, 'reviewed')} className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 ml-1">Reviewed</button>
              )}
              {s.status === 'reviewed' && (
                <button title="Mark converted" onClick={() => onStatus(s, 'converted')} className="text-xs px-2 py-1 rounded bg-purple-50 text-purple-700 hover:bg-purple-100 ml-1">Converted</button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Th = ({ children, className }) => <th className={`px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide ${className || ''}`}>{children}</th>;

const CreateLinkModal = ({ link, setLink, onCancel, onCreate }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onCancel}>
    <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
      <h2 className="text-lg font-bold text-gray-900 mb-1">New intake link</h2>
      <p className="text-xs text-gray-500 mb-4">
        <b>When to send:</b> after the quote is accepted and the deposit received, ideally 4-6 weeks before travel so there's runway for rabies boosters + titre timing. For UK / EU / Australia / Hawaii clients, send as soon as the booking is confirmed — the rabies titre must pass 3 months before entry.
      </p>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-xs font-semibold text-gray-600 mb-1">Client Name</span>
          <input value={link.client_name} onChange={e => setLink(l => ({ ...l, client_name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" placeholder="Full name" data-testid="new-link-name" />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold text-gray-600 mb-1">Client Email</span>
          <input value={link.client_email} onChange={e => setLink(l => ({ ...l, client_email: e.target.value }))} type="email" className="w-full px-3 py-2 border rounded-lg" placeholder="client@example.com" data-testid="new-link-email" />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold text-gray-600 mb-1">Client WhatsApp / Phone (with country code)</span>
          <input value={link.client_phone} onChange={e => setLink(l => ({ ...l, client_phone: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" placeholder="+1 345 555 0100" data-testid="new-link-phone" />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold text-gray-600 mb-1">Expires in (days)</span>
          <input value={link.expires_in_days} onChange={e => setLink(l => ({ ...l, expires_in_days: parseInt(e.target.value) || 30 }))} type="number" className="w-full px-3 py-2 border rounded-lg" min="1" max="365" />
        </label>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onCancel} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
        <button onClick={onCreate} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="confirm-create-link">Create link</button>
      </div>
    </div>
  </div>
);

const ViewSubmissionModal = ({ sub, onClose, onPdf, onAuditPdf, onDelete, onStatus, onConvert }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between p-5 border-b">
        <div>
          <div className="text-xs text-gray-500">Submission · {sub.id.slice(0, 8)}</div>
          <h2 className="text-lg font-bold text-gray-900">{sub.client_name || sub.consignor?.full_name || '—'}</h2>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={sub.status} />
          <button onClick={() => onPdf(sub)} className="text-sm px-3 py-1.5 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 flex items-center gap-1"><FileDown className="w-4 h-4" /> PDF</button>
          <button onClick={() => onDelete(sub)} className="text-sm px-2 py-1.5 rounded text-red-600 hover:bg-red-50" title="Delete"><Trash2 className="w-4 h-4" /></button>
          <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="p-5 overflow-y-auto space-y-4 text-sm">
        {((sub.consignors && sub.consignors.length) ? sub.consignors : (sub.consignor ? [sub.consignor] : [])).map((c, i) => (
          <KV key={`consignor-${i}`} title={`Consignor ${i + 1}`} data={c} />
        ))}
        {((sub.consignees && sub.consignees.length) ? sub.consignees : (sub.consignee ? [sub.consignee] : [])).map((c, i) => (
          <KV key={`consignee-${i}`} title={`Consignee ${i + 1}`} data={c} />
        ))}
        <KV title="Travel" data={sub.travel} />
        <div>
          <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">Pets ({sub.pets?.length || 0})</div>
          {(sub.pets || []).map((p, i) => (
            <div key={i} className="border rounded-xl p-3 mb-2 bg-gray-50/50">
              <div className="font-semibold text-gray-900">{p.name} <span className="text-xs font-normal text-gray-500">· {p.species} · {p.breed} · {p.sex_status}</span></div>
              <div className="text-xs text-gray-600 mt-1">Microchip: {p.microchip_number || '—'} · DOB: {p.dob || '—'} · Weight: {p.weight || '—'}</div>
              {p.cayman_passport?.holder && <div className="text-xs text-emerald-700 mt-1">CI Passport #{p.cayman_passport.number} · renewed {p.cayman_passport.issue_or_renewal_date}</div>}
              {p.rabies?.upload_paths?.length > 0 && <div className="text-xs mt-1">Rabies certs: {p.rabies.upload_paths.length} attached</div>}
              {p.titre?.upload_paths?.length > 0 && <div className="text-xs mt-1">Titre certs: {p.titre.upload_paths.length} attached</div>}
              {p.medications?.length > 0 && <div className="text-xs mt-1">Meds: {p.medications.map(m => m.name).join(', ')}</div>}
              {p.notes && <div className="text-xs text-gray-700 mt-2 italic">"{p.notes}"</div>}
            </div>
          ))}
        </div>
        <KV title="Emergency Contact" data={sub.emergency_contact} />
        <div>
          <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2 flex items-center justify-between">
            <span>Signature</span>
            {sub.signature?.typed_name && (
              <button
                onClick={() => onAuditPdf(sub)}
                data-testid="intake-signature-audit-btn"
                className="text-xs px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-1"
                title="Download signature certificate of completion (typed name, cursive, IP, timestamp, audit trail)"
              >
                <FileDown className="w-3.5 h-3.5" /> Audit PDF
              </button>
            )}
          </div>
          <div style={{ fontFamily: '"Dancing Script", cursive', fontSize: '32px', color: '#1f2937' }}>{(sub.signature?.typed_name || '').split(',')[0].trim()}</div>
          <div className="text-sm font-semibold">{sub.signature?.typed_name}</div>
          <div className="text-xs text-gray-500 italic">Signed on {sub.signature?.signed_at?.slice(0, 19).replace('T', ' ')} UTC</div>
          {sub.signature?.ip && <div className="text-xs text-gray-500">IP {sub.signature.ip}</div>}
          {sub.signature?.document_id && <div className="text-xs text-gray-500">Document ID: <span className="font-mono">{sub.signature.document_id}</span></div>}
        </div>
      </div>
      <div className="p-4 border-t flex justify-end gap-2 flex-wrap">
        {sub.status !== 'converted' && (
          <button
            onClick={() => onConvert(sub)}
            className="text-sm px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-1.5"
            data-testid="convert-to-relocation-btn"
          >
            <ArrowRightCircle className="w-4 h-4" /> Convert to Relocation
          </button>
        )}
        {sub.status === 'converted' && sub.conversion && (
          <div className="w-full flex flex-wrap gap-2 justify-end">
            <div className="text-sm px-3 py-1.5 rounded bg-purple-50 text-purple-700 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Converted — {sub.conversion.relocation_ids?.length || 0} relocation(s), {sub.conversion.pet_ids?.length || 0} pet(s){sub.conversion.document_ids?.length ? `, ${sub.conversion.document_ids.length} doc(s) filed` : ''}
            </div>
            {(sub.conversion.relocation_ids || []).map((rid) => (
              <a
                key={rid}
                href={`/admin/relocations/${rid}`}
                className="text-sm px-3 py-1.5 rounded bg-purple-100 hover:bg-purple-200 text-purple-800 font-medium flex items-center gap-1.5"
              >
                → Relocation {rid.slice(0, 6)} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ))}
          </div>
        )}
        {sub.status === 'new' && <button onClick={() => onStatus(sub, 'reviewed')} className="text-sm px-3 py-1.5 rounded bg-blue-50 hover:bg-blue-100 text-blue-700">Mark reviewed</button>}
        {sub.status === 'reviewed' && <button onClick={() => onStatus(sub, 'converted')} className="text-sm px-3 py-1.5 rounded bg-purple-50 hover:bg-purple-100 text-purple-700">Mark converted</button>}
      </div>
    </div>
  </div>
);

const KV = ({ title, data }) => {
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {Object.entries(data).map(([k, v]) => (
          <div key={k}><span className="text-gray-500">{k.replace(/_/g, ' ')}:</span> <span className="text-gray-900">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v || '—')}</span></div>
        ))}
      </div>
    </div>
  );
};
